import { allLanguages } from "../../../../../shared/consts";
import type {
  CaregiverStaffingPreferenceField,
  CaregiverStaffingPreferences,
  CaregiverStaffingPreferencesBody,
} from "./staffingPreferences.types";

export const caregiverStaffingLanguageOptions = allLanguages.map((language) => ({
  value: language,
  label: language,
}));

export const caregiverSecondaryLanguageOptions = [
  "English",
  "Spanish",
  "Chinese",
  "Portuguese",
  "French",
  "Russian",
  "Arabic",
].map((language) => ({ value: language, label: language }));

export const caregiverTransportationOptions = [
  "Car",
  "Public transportation",
  "Walking",
  "Bicycle",
].map((transportationType) => ({ value: transportationType, label: transportationType }));

export const caregiverDrivingTimeOptions = [30, 45, 60, 75, 90, 120, 135, 150].map((minutes) => ({
  value: minutes,
  label: String(minutes),
}));

export const caregiverWorkTimeOptions = ["During the week", "Weekends", "Both"].map((workTime) => ({
  value: workTime,
  label: workTime,
}));

export const caregiverCasesLengthOptions = [
  "Long permanent cases",
  "Short replacements",
  "Live-in cases",
].map((caseLength) => ({ value: caseLength, label: caseLength }));

export const caregiverPatientsTypeOptions = [
  "Pediatric patients",
  "Geriatric patients",
  "Adolescent patients",
  "Bed-bound patients",
  "Patients that require minimal assistance",
].map((patientType) => ({ value: patientType, label: patientType }));

export function createCaregiverStaffingPreferencesDraft(
  preferences: CaregiverStaffingPreferences
): CaregiverStaffingPreferencesBody {
  return {
    additionalInfo: preferences.additionalInfo ?? null,
    casesLength: preferences.casesLength ?? null,
    languages: preferences.languages ?? null,
    mainLanguage: preferences.mainLanguage ?? null,
    patientsTypes: preferences.patientsTypes ?? null,
    publicTransprotationTimeLimit: preferences.publicTransprotationTimeLimit ?? null,
    smokingCaregiver: preferences.smokingCaregiver ?? null,
    studying: preferences.studying ?? null,
    studyingTimes: preferences.studyingTimes ?? null,
    transportationType: preferences.transportationType ?? null,
    workTimes: preferences.workTimes ?? null,
    workWithBedbound: preferences.workWithBedbound ?? null,
    workWithHalal: preferences.workWithHalal ?? null,
    workWithHoyerLift: preferences.workWithHoyerLift ?? null,
    workWithKids: preferences.workWithKids ?? null,
    workWithKosher: preferences.workWithKosher ?? null,
    workWithPets: preferences.workWithPets ?? null,
    workWithShabat: preferences.workWithShabat ?? null,
    workWithSmokingPatient: preferences.workWithSmokingPatient ?? null,
    workWithWalkingAsistant: preferences.workWithWalkingAsistant ?? null,
  };
}

export function formatCaregiverStaffingValue(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "Unknown";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "Unknown";
  }

  return String(value);
}

export function getNullableBoolean(value: unknown) {
  return typeof value === "boolean" ? value : null;
}

export function normalizeOptionalText(value: string) {
  return value.trim() === "" ? null : value;
}

export const caregiverStaffingPreferenceColumns: CaregiverStaffingPreferenceField[][] = [
  [
    { label: "Is caregiver smoking", key: "smokingCaregiver", type: "boolean" },
    { label: "Work with a smoking patient", key: "workWithSmokingPatient", type: "boolean" },
    { label: "Main language", key: "mainLanguage", type: "select" },
    { label: "Secondary languages", key: "languages", type: "multiselect" },
    { label: "Preferred transportation type", key: "transportationType", type: "select" },
  ],
  [
    {
      label: "Maximum driving time (minutes)",
      key: "publicTransprotationTimeLimit",
      type: "select",
    },
    { label: "Work in a house with kids", key: "workWithKids", type: "boolean" },
    { label: "Working with Hoyer lift", key: "workWithHoyerLift", type: "boolean" },
    { label: "Work with pets", key: "workWithPets", type: "boolean" },
    { label: "Experience with a bedbound patient", key: "workWithBedbound", type: "boolean" },
  ],
  [
    {
      label: "Experience with walking assistance need patient",
      key: "workWithWalkingAsistant",
      type: "boolean",
    },
    { label: "Experience working at a Kosher home", key: "workWithKosher", type: "boolean" },
    { label: "Experience working with the halal culture", key: "workWithHalal", type: "boolean" },
    {
      label: "Experience working with a Shabbat observant",
      key: "workWithShabat",
      type: "boolean",
    },
  ],
];

export const caregiverStaffingDetailColumns: CaregiverStaffingPreferenceField[][] = [
  [
    {
      label: "Are you looking for long permanent cases or short replacements?",
      key: "casesLength",
      type: "multiselect",
    },
    { label: "When are you looking to work?", key: "workTimes", type: "select" },
    { label: "Are you currently working or studying?", key: "studying", type: "boolean" },
    { label: "When are you working/studying?", key: "studyingTimes", type: "textarea" },
  ],
  [
    {
      label: "What patients are you willing to work with?",
      key: "patientsTypes",
      type: "multiselect",
    },
    {
      label:
        "Is there anything else you would like to bring to our attention regarding your preferences?",
      key: "additionalInfo",
      type: "textarea",
    },
  ],
];
